import type { TypedUseSelectorHook } from "react-redux";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import type { RootState, AppDispatch } from "./store";
import {
  type ClimateOption,
  type InputName,
  type Option,
  type Submodule,
} from "../types/modulesInterfaces";
import {
  setCurrentModuleThreads,
} from "./features/builder/builderSlice";
import { checkErrorsTouched } from "@shared/modulePayload";
import type { Errors, Touched } from "@shared/modulePayload";
import { selectCurrentProject } from "./features/project/projectSlice";
import { Role } from "../types/interfaces";
import { selectCurrentUser } from "./features/auth/authSlice";
import { ChangesResponseType } from "../components/actions/changes/changesTypes";
import { useTranslation } from "react-i18next";
import { getThreadIds } from "@shared/modulePayload";
import { useGetClimateTypesQuery, useGetMoistureTypesQuery } from "./features/dropdownOptions/dropdownOptionsApiSlice";
import { useFormikContext } from "formik";
import { BEModulesClassNames, fetchLandUseTypesHybrid } from "../utils/moduleList";
import { NavigateOptions, useLocation, useNavigate, useParams } from "react-router-dom";
import { decodeProjectId, isEncodedProjectId, decodeActivityId } from "../utils/projectIdEncoder";

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch; //to dispatch reducers
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector; //to get states

export const useAppParams = () => {
  const params = useParams<string>();
  const location = useLocation();
  const { projectId: rawProjectId, activityId: rawActivityId, tab } = params;

  // Handle project ID decoding for public routes
  const projectId = useMemo(() => {
    if (!rawProjectId) return rawProjectId;

    // Check if we're in a public route and the projectId is encoded
    const isPublicRoute = location.pathname.includes('/public');
    if (isPublicRoute && rawProjectId !== 'new') {
      try {
        // Try to decode the project ID
        if (isEncodedProjectId(rawProjectId)) {
          const decodedId = decodeProjectId(rawProjectId);
          return decodedId.toString();
        }
      } catch (error) {
        console.error('Failed to decode project ID:', error);
        // Fall back to raw value if decoding fails
      }
    }

    return rawProjectId;
  }, [rawProjectId, location.pathname]);

  // Handle activity ID decoding for public routes
  const activityId = useMemo(() => {
    if (!rawActivityId) return rawActivityId;

    // Check if we're in a public route and the activityId is encoded
    const isPublicRoute = location.pathname.includes('/public');
    if (isPublicRoute) {
      try {
        // Try to decode the activity ID
        if (isEncodedProjectId(rawActivityId)) { // Using same validation function since format is identical
          const decodedId = decodeActivityId(rawActivityId);
          return decodedId.toString();
        }
      } catch (error) {
        console.error('Failed to decode activity ID:', error);
        // Fall back to raw value if decoding fails
      }
    }

    return rawActivityId;
  }, [rawActivityId, location.pathname]);

  return { projectId, activityId, tab, rawProjectId, rawActivityId }
}

export const useAppNavigate = () => {
  const navigate = useNavigate();
  const { isPublic } = usePermissions();

  const appNavigate = useCallback(({ path, options, ignorePublic }: { path: string, options?: NavigateOptions, ignorePublic?: boolean }) => {
    if (isPublic && !ignorePublic) {
      navigate(`/public${path}`, options);
    } else {
      navigate(path, options);
    }
  }, [isPublic, navigate]);

  return appNavigate;
}

interface WindowDimentions {
  width: number;
  height: number;
}

function getWindowDimentions(): WindowDimentions {
  const { innerWidth: width, innerHeight: height } = window;
  return { width, height };
}

export const useWindowDimetions = (): WindowDimentions => {
  const [windowDimentions, setWindowDimetions] = useState(getWindowDimentions);
  useEffect(() => {
    const handleResize = () => setWindowDimetions(getWindowDimentions());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimentions;
};

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}



//TODO: replace any in VariableSubmodule and think better solution
interface VariableSubmodule {
  data: any,
  schema: Submodule | null
}
/**
 * useSyncModuleWithStore
 * Performs side effects without returning values
 * Dispatches notes and comments necessary data to the store
 *
 * @param T1Data - Module Full data without tiertwo
 * @param moduleSchema - This will be used to create entities to render comments. 
 * @param isT1DataLoading - We don't perform side effects till we have api data. This should be true when the respective hook for the values in T1Data is loading
 * @param notes - We just dispatch notes to the store
 * @param variableSubmodules - A variable Submodule means the user can add as per his needs. We need to create a comment group for each submodule created. It takes the schema and the api data for the variable submodule.
 * @returns {void} 
 */
export const useThreadsProcessor = <T>() => {
  const dispatch = useAppDispatch();

  const processModuleThreads = useCallback((moduleData?: Partial<T> | null) => {
    if (!moduleData) return;
    const threadIds = getThreadIds<T>(moduleData);
    dispatch(setCurrentModuleThreads(threadIds))
  }, [dispatch]);

  const resetThreads = useCallback(() => {
    dispatch(setCurrentModuleThreads([]))
  }, [dispatch])

  useEffect(() => {
    return () => resetThreads()
  }, [dispatch, resetThreads]);

  return {
    processModuleThreads,
  }
};


export const useScrollToElement = (offset = -450) => {
  const pulseAnimate = (element: HTMLElement) => {
    element.style.transition = 'transform 1s';
    element.style.transform = 'scale(1.5)';

    setTimeout(() => {
      element.style.transition = 'transform 0.5s';
      element.style.transform = 'scale(1)';
    }, 1000)
  }

  const scrollToElementById = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition + offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      if (element.tagName !== 'ARTICLE') pulseAnimate(element)
    }
  }, [offset]);
  return {
    scrollToElementById
  }
}

export const useHasFormErrors = ({ errors, touched }: { errors: Errors, touched: Touched }) => {
  const hasFormError = useMemo(() => checkErrorsTouched(errors, touched), [errors, touched])

  return hasFormError
}
export const useWarningMessageT2 = () => {
  const [warningMessages, setWarningMessages] = useState<{ id: string, message: string }[]>([]);
  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current.clear();
    };
  }, []);

  // Add a new warning message with unique ID
  const handleDefaultsError = useCallback((message: string) => {
    setWarningMessages(prev => {
      // Check if this exact message already exists in the warnings
      const isDuplicate = prev.some(existing => existing.message === message);
      if (isDuplicate) {
        return prev; // Skip duplicate messages
      }

      const newMessage = {
        id: Date.now().toString(),
        message
      };

      // Set a timeout to remove this specific message after 15 seconds
      const timeoutId = setTimeout(() => {
        setWarningMessages(current => current.filter(item => item.id !== newMessage.id));
        timeoutRefs.current.delete(timeoutId);
      }, 15000);

      timeoutRefs.current.add(timeoutId);

      return [...prev, newMessage];
    });
  }, []);

  // For backward compatibility
  const warningMessage = warningMessages.length > 0 ? warningMessages[0].message : null;

  // Function to manually clear all warning messages
  const clearAllWarnings = useCallback(() => {
    // Clear all timeouts
    timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current.clear();
    setWarningMessages([]);
  }, []);

  return {
    warningMessage,             // For backward compatibility
    warningMessages,            // The list of warning messages
    handleDefaultsError,        // Add a new warning that auto-dismisses
    clearAllWarnings            // Clear all warnings
  };
};


export const usePermissions = () => {
  const { project } = useAppSelector(selectCurrentProject);
  const { pathname } = useLocation();
  const user = useAppSelector(selectCurrentUser);

  const userRoles = useMemo(() => project?.role || [], [project?.role]);

  const isUserRole = useCallback((role: Role): boolean =>
    userRoles.includes(role), [userRoles]);

  const isAdmin = useCallback((): boolean => isUserRole(Role.ADMIN), [isUserRole]);
  const isAnalyst = useCallback((): boolean => isUserRole(Role.ANALYST), [isUserRole]);
  const isReceiver = useCallback((): boolean => isUserRole(Role.RECEIVER), [isUserRole]);
  const isSecondReviewer = useCallback((): boolean => isUserRole(Role.REVIEWER), [isUserRole]);

  const isFinalized = useMemo(() => project?.is_finalized ?? false, [project]);
  const isPublic = useMemo(() =>
    (!project || project?.is_public) && pathname.includes("public"),
    [project, pathname]);
  const isAdminOrAnalyst = useMemo(() =>
    isAdmin() || isAnalyst(),
    [isAdmin, isAnalyst]);
  const isReadOnly = useMemo(() =>
    (!isAdminOrAnalyst && isReceiver()) || isFinalized || isPublic,
    [isAdminOrAnalyst, isReceiver, isFinalized, isPublic]);

  const canMutateComment = useCallback((commentOwnerId: number): boolean => {
    if (isAnalyst() || isSecondReviewer())
      return commentOwnerId === user?.id;
    return isAdmin();
  }, [isAnalyst, isAdmin, user?.id]);

  return {
    canMutateComment,
    canMutateProject: isAdminOrAnalyst || !project,
    canMutateNote: isAdminOrAnalyst,
    canShare: isAdminOrAnalyst,
    isReadOnly,
    isFinalized,
    isPublic,
    isAdmin,
    isAnalyst,
    isReceiver,
    isUserRole,
  };
};

export const useCustomTranslation = (key: string | undefined): string | null => {
  const { t, i18n } = useTranslation();
  if (!key || typeof key !== 'string') return null
  const t2 = i18n.exists(key) ? t(key) : key
  return t2
}

export const useCustomTranslationHandler = (): (key: string | undefined) => string | null => {
  const { t, i18n } = useTranslation();

  return (key: string | undefined) => {
    if (!key) return null
    const t2 = i18n.exists(key) ? t(key) : key
    return t2
  }
}

export const useGetAppLanguage = () => {
  const { i18n } = useTranslation()
  const { language } = i18n
  function languageOptionKey(): keyof Omit<Option, "id"> {
    switch (language) {
      case "en":
        return "name_en"
      case "fr":
        return "name_fr"
      case "es":
        return "name_es"
      case "ru":
        return "name_ru"
      default:
        return "name"
    }
  }
  return { language, apiLanguageField: languageOptionKey() }
}


interface SearchProps {
  list: ChangesResponseType[],
  searchString: string
}

// Case-insensitive substring match across the searchable fields of a change
// entry (replaces fuse.js fuzzy search — exact substring, no score ranking).
const matchesSearch = (item: ChangesResponseType, query: string): boolean => {
  const haystack = [
    item.reason,
    item.user,
    ...item.changes.flatMap((c) => [c.field, c.old, c.new]),
  ];
  return haystack.some((field) => field?.toLowerCase().includes(query));
};

export const useSearch = ({ list, searchString }: SearchProps) => {
  const [searchResults, setSearchResults] = useState<ChangesResponseType[]>([])
  useEffect(() => {
    if (list?.length && searchString.length) {
      const query = searchString.toLowerCase();
      setSearchResults(list.filter((item) => matchesSearch(item, query)));
    } else if (list?.length) {
      setSearchResults(list)
    }
  }, [list, searchString])

  return { searchResults }
}

export function useClimateAndMoistureFormHandlers() {
  const { setFieldValue } = useFormikContext();
  const { data: climateTypes } = useGetClimateTypesQuery();
  const { data: moistureTypes } = useGetMoistureTypesQuery();

  const handleClimateInputChange = (e: React.ChangeEvent<HTMLInputElement>, moisture: number | null | undefined, moistureFieldName: string) => {
    const climateChosen = e.target.value;
    const filteredMoistures = getMoisturesByClimate(climateTypes, +climateChosen, moistureTypes);
    if (!filteredMoistures.find(opt => opt.id === moisture) && moisture) {
      setFieldValue(moistureFieldName, null);
    }
  };

  const getMoistureOptions = (climate: number | null | undefined) => {
    return getMoisturesByClimate(climateTypes, climate, moistureTypes);
  }

  function getMoisturesByClimate(
    climateTypes: ClimateOption[] | undefined,
    climateChosen: number | null | undefined,
    moistureTypes: Option[] | undefined
  ): Option[] {
    if (!climateChosen) return moistureTypes ?? [];

    const climateType = climateTypes?.find(c => c.id === climateChosen);
    return climateType
      ? moistureTypes?.filter(m => climateType.moistures.includes(m.id)) ?? []
      : moistureTypes ?? [];
  }

  return {
    climateTypes,
    moistureTypes,
    handleClimateInputChange,
    getMoistureOptions,
    setFieldValue,
  };
}

export function useFetchLucTypesHybrid({
  beModuleId,
  className,
}: {
  beModuleId?: number;
  className?: BEModulesClassNames;
}) {
  const { activity, project } = useAppSelector(selectCurrentProject);
  const landUseTypes = fetchLandUseTypesHybrid({ beModuleId, className, activity, project });

  return { landUseTypes };
}

export function useInputNameHandler({ inputName, index }: { inputName?: InputName, index?: number }) {
  const [name, setName] = useState<string>("");

  useEffect(() => {
    if (inputName) setName(typeof inputName === "function" ? inputName(index ?? 0) : inputName)
  }, [index, inputName])

  return { name, isFunction: typeof inputName === "function" }
}

export const useIconSize = (size: number = 20) => {
  const { width } = useWindowDimetions();
  const ratio = useMemo(() => {
    if (width > 2560) return 1.65;
    if (width > 1920) return 1.3;
    return 1;
  }, [width]);

  return size * ratio;
}

export const useFontSize = (size: number = 16) => {
  const { width } = useWindowDimetions();
  const ratio = useMemo(() => {
    if (width > 2560) return 1.5;
    if (width > 1920) return 1.25;
    return 1;
  }, [width]);

  return size * ratio;
}